/**
 * Sentry initialization for paver-frontend.
 * This file must be imported FIRST in main.tsx — before React, before App.
 *
 * SDK: @sentry/react (Vite-native, no Next.js shim needed)
 *
 * SECURITY (audit #7): Session Replay masks ALL text, inputs, and media to
 * prevent leaking PII, client emails, pricing, signatures, and uploaded photos
 * into Sentry. The beforeSend / beforeBreadcrumb hooks also strip Authorization
 * headers and `?token=` query params from captured requests/URLs/breadcrumbs
 * (impersonate, auto-login, password-reset, and welcome flows all use ?token=JWT).
 */

import * as Sentry from '@sentry/react'

/**
 * Remove the `token` query param from a URL string. Returns the original
 * input if it cannot be parsed as a URL (e.g. a relative path with no base).
 * Exported for unit tests.
 */
export function stripTokenFromUrl(url: string | undefined): string | undefined {
  if (!url) return url
  try {
    // Use a placeholder base so relative paths still parse.
    const isAbsolute = /^https?:\/\//i.test(url)
    const parsed = new URL(url, isAbsolute ? undefined : 'http://placeholder.local')
    if (parsed.searchParams.has('token')) {
      parsed.searchParams.set('token', '[Filtered]')
    }
    if (isAbsolute) {
      return parsed.toString()
    }
    // Reconstruct relative form: path + search + hash
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return url
  }
}

/**
 * Strip sensitive headers (Authorization, Cookie, Proxy-Authorization, set-cookie)
 * from a headers bag. Mutates a shallow copy. Exported for unit tests.
 */
export function stripSensitiveHeaders(
  headers: Record<string, string | string[]> | undefined
): Record<string, string | string[]> | undefined {
  if (!headers) return headers
  const cleaned: Record<string, string | string[]> = { ...headers }
  for (const key of Object.keys(cleaned)) {
    const lower = key.toLowerCase()
    if (
      lower === 'authorization' ||
      lower === 'cookie' ||
      lower === 'set-cookie' ||
      lower === 'proxy-authorization' ||
      lower === 'x-auth-token'
    ) {
      cleaned[key] = '[Filtered]'
    }
  }
  return cleaned
}

/**
 * Sentry `beforeSend` hook — strips auth headers from the request and
 * removes `?token=` from the request URL on every captured event.
 * Exported for unit tests.
 */
export function sanitizeEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.request) {
    if (event.request.url) {
      event.request.url = stripTokenFromUrl(event.request.url)
    }
    if (event.request.headers) {
      event.request.headers = stripSensitiveHeaders(
        event.request.headers as Record<string, string | string[]>
      ) as typeof event.request.headers
    }
  }
  return event
}

/**
 * Sentry `beforeBreadcrumb` hook — strips `?token=` from navigation,
 * fetch, and xhr breadcrumb URLs so JWTs in welcome/impersonate/reset
 * links never reach Sentry.
 * Exported for unit tests.
 */
export function sanitizeBreadcrumb(
  breadcrumb: Sentry.Breadcrumb
): Sentry.Breadcrumb | null {
  if (!breadcrumb.data) return breadcrumb
  const data = breadcrumb.data as Record<string, unknown>
  if (typeof data.url === 'string') {
    data.url = stripTokenFromUrl(data.url)
  }
  if (typeof data.to === 'string') {
    data.to = stripTokenFromUrl(data.to)
  }
  if (typeof data.from === 'string') {
    data.from = stripTokenFromUrl(data.from)
  }
  return breadcrumb
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.PROD,

  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
      maskAllInputs: true,
    }),
  ],

  tracesSampleRate: 0.05,

  tracePropagationTargets: [
    'localhost',
    /^https:\/\/paver-api\.vercel\.app/,
    /^https:\/\/app\.getpaver\.io/,
  ],

  replaysSessionSampleRate: 0.02,
  replaysOnErrorSampleRate: 1.0,

  environment: import.meta.env.MODE,

  debug: false,

  beforeSend: sanitizeEvent,
  beforeBreadcrumb: sanitizeBreadcrumb,
})
